import { useNavigate } from "react-router-dom";
import { Tooltip } from "@heroui/react";

// Robinson projection onto the 950x620 Wikimedia low-res world map.
const TABLE = [[0, 1, 0], [5, 0.9986, 0.062], [10, 0.9954, 0.124], [15, 0.99, 0.186], [20, 0.9822, 0.248], [25, 0.973, 0.31], [30, 0.96, 0.372], [35, 0.9427, 0.434], [40, 0.9216, 0.4958], [45, 0.8962, 0.5571], [50, 0.8679, 0.6176], [55, 0.835, 0.6769], [60, 0.7986, 0.7346], [65, 0.7597, 0.7903], [70, 0.7186, 0.8435], [75, 0.6732, 0.8936], [80, 0.6213, 0.9394], [85, 0.5722, 0.9761], [90, 0.5322, 1]];
const R = 950 / (2 * 0.8487 * Math.PI);
const OX = -6, OY = 8;
export function project(lon, lat) {
  const a = Math.abs(lat), i = Math.min(17, Math.floor(a / 5)), f = (a - i * 5) / 5;
  const X = TABLE[i][1] + (TABLE[i + 1][1] - TABLE[i][1]) * f, Y = TABLE[i][2] + (TABLE[i + 1][2] - TABLE[i][2]) * f;
  return [475 + OX + 0.8487 * R * X * (lon * Math.PI / 180), 310 + OY - Math.sign(lat) * 1.3523 * R * Y];
}

const arc = (hub, [x, y]) => { const cx = (hub[0] + x) / 2, cy = Math.min(hub[1], y) - Math.abs(hub[0] - x) * 0.18; return `M${hub[0]},${hub[1]} Q${cx},${cy} ${x},${y}`; };
const short = (s) => (s || "").replace(/\s*\(.*\)/, "");
// Label offsets for crowded regions (dx, dy, anchor)
const LABEL_FIX = { "4.9,52.4": [-7, -4, "end"], "13.4,52.5": [7, 8, "start"], "12.5,41.9": [-7, 10, "end"], "23.7,38.0": [-6, 13, "end"], "-3.7,40.4": [-7, 4, "end"], "31.2,30.0": [-8, 12, "end"], "28.9,41.0": [7, -7, "start"], "114.2,22.3": [7, 9, "start"], "139.7,35.7": [7, -3, "start"] };
const labelPos = (lon, lat) => LABEL_FIX[`${lon},${lat}`] || [7, 3.5, "start"];

export function SupplyMap({ network, reduce }) {
  const navigate = useNavigate();
  const hub = project(network.hub.lon, network.hub.lat);
  const seen = new Map();
  network.routes.forEach((r) => { const k = `${r.lon},${r.lat}`; const prev = seen.get(k); seen.set(k, { ...r, n: (prev?.n || 0) + 1, list: [...(prev?.list || []), r] }); });
  const routes = [...seen.values()];
  const activeKeys = new Set(routes.map((r) => `${r.lon},${r.lat}`));
  const markets = (network.markets || []).filter((m) => !activeKeys.has(`${m.lon},${m.lat}`));
  const redacted = !!network.redacted;
  return (
    <svg viewBox="0 0 950 620" className={`w-full h-auto ${reduce ? "" : "hd-map-animate"}`} role="img" aria-label="Global supply network" data-testid="supply-map">
      <image href="/world.svg" width="950" height="620" opacity="0.95" />
      {markets.map((m, i) => {
        const [x, y] = project(m.lon, m.lat), [dx, dy, anchor] = labelPos(m.lon, m.lat), wh = m.kind === "warehouse";
        return (
          <Tooltip key={`m${i}`} content={`${m.name} · ${wh ? "warehouse facility" : m.shipments ? `${m.shipments} shipment${m.shipments > 1 ? "s" : ""} to date` : "market served"}`} size="sm">
            <g className="cursor-default" data-testid={wh ? `map-warehouse-${i}` : `map-market-${i}`}>
              {wh ? (<>
                <circle cx={x} cy={y} r="11" fill="#F5C64D" fillOpacity="0.18" />
                <rect x={x - 5} y={y - 5} width="10" height="10" rx="1.5" fill="#0D1B2A" stroke="#F5C64D" strokeWidth="1.8" />
                <path d={`M${x - 3.2},${y + 2.6} v-3.6 h2.2 v3.6 M${x + 1},${y + 2.6} v-3.6 h2.2 v3.6`} stroke="#F5C64D" strokeWidth="0.9" fill="none" />
              </>) : (<>
                <circle cx={x} cy={y} r="7" fill="#0D1B2A" fillOpacity="0.08" />
                <circle cx={x} cy={y} r="3.2" fill="#fff" stroke="#0D1B2A" strokeWidth="1.6" />
              </>)}
              <text x={x + (wh ? dx + 3 : dx)} y={y + dy} fontSize={wh ? "10.5" : "9.5"} fontWeight={wh ? "700" : "600"} fill={wh ? "#0D1B2A" : "#334155"} opacity={wh ? 1 : 0.85} textAnchor={anchor}>{short(m.name)}{wh ? " · WH" : ""}</text>
            </g>
          </Tooltip>
        );
      })}
      {routes.map((r, i) => {
        const p = project(r.lon, r.lat), d = arc(hub, p), id = `route-${i}`;
        return (
          <g key={i} className={redacted ? "" : "cursor-pointer"} onClick={() => { if (!redacted) navigate(r.n === 1 ? `/shipments/${r.shipment_id}` : "/shipments?status=active"); }} data-testid={`map-route-${i}`}>
            <path id={id} d={d} fill="none" stroke="#377DD3" strokeWidth="1.4" strokeDasharray="4 5" strokeOpacity="0.8" className="hd-route" style={{ animationDelay: `${i * 0.5}s` }} />
            {!reduce && (
              <g data-testid={`map-parcel-${i}`}>
                <rect x="-5" y="-5" width="10" height="10" rx="2" fill="#F5C64D" stroke="#0D1B2A" strokeWidth="1.2">
                  <animateMotion dur={`${5 + (i % 3)}s`} repeatCount="indefinite" begin={`${i * 0.7}s`} rotate="auto"><mpath href={`#${id}`} /></animateMotion>
                </rect>
              </g>
            )}
            <Tooltip content={<div className="text-xs"><p className="font-semibold">{r.to} · {r.n} parcel{r.n > 1 ? "s" : ""} en route</p>{!redacted && r.list.slice(0, 4).map((s) => <p key={s.shipment_id}>{s.shipment_number} · {(s.status || "").replace(/_/g, " ")}</p>)}{!redacted && r.n > 4 && <p>+{r.n - 4} more</p>}</div>} size="sm">
              <g>
                <circle cx={p[0]} cy={p[1]} r="11" fill="#377DD3" fillOpacity="0.15" className="hd-hub-pulse" />
                <circle cx={p[0]} cy={p[1]} r="4.5" fill="#377DD3" stroke="#fff" strokeWidth="1.5" />
                {r.n > 1 && <text x={p[0]} y={p[1] + 3} fontSize="7" fontWeight="800" fill="#fff" textAnchor="middle">{r.n}</text>}
                <text x={p[0] + 9} y={p[1] - 6} fontSize="11" fontWeight="700" fill="#0D1B2A">{short(r.to)}</text>
              </g>
            </Tooltip>
          </g>
        );
      })}
      <g data-testid="map-hub">
        <circle cx={hub[0]} cy={hub[1]} r="16" fill="#F5C64D" fillOpacity="0.22" className="hd-hub-pulse" />
        <circle cx={hub[0]} cy={hub[1]} r="6" fill="#F5C64D" stroke="#fff" strokeWidth="2" />
        <text x={hub[0] + 12} y={hub[1] - 10} fontSize="14" fontWeight="700" fill="#0D1B2A">Dubai</text>
      </g>
      <g transform="translate(14, 584)" fontSize="10" fill="#475569" data-testid="map-legend">
        <rect x="-6" y="-14" width="470" height="24" rx="6" fill="#fff" fillOpacity="0.8" />
        <circle cx="4" cy="-2" r="4" fill="#F5C64D" stroke="#fff" strokeWidth="1.5" /><text x="12" y="1.5">Dubai hub</text>
        <rect x="70" y="-6.5" width="9" height="9" rx="1.5" fill="#0D1B2A" stroke="#F5C64D" strokeWidth="1.5" /><text x="84" y="1.5">Warehouse facility</text>
        <rect x="178" y="-6.5" width="9" height="9" rx="2" fill="#F5C64D" stroke="#0D1B2A" strokeWidth="1" /><text x="192" y="1.5">Parcel en route</text>
        <circle cx="276" cy="-2" r="4" fill="#377DD3" stroke="#fff" strokeWidth="1.2" /><text x="284" y="1.5">Active destination</text>
        <circle cx="376" cy="-2" r="3" fill="#fff" stroke="#0D1B2A" strokeWidth="1.4" /><text x="383" y="1.5">Market served</text>
      </g>
    </svg>
  );
}
